import React, { useEffect } from 'react';
import '@atlaskit/css-reset';
import { type PreloadedQuery, usePreloadedQuery, useQueryLoader, graphql } from 'react-relay';
import { ProjectBreadcrumb } from '@atlassian/jira-breadcrumbs/src';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import { setMark, setMeasure } from '@atlassian/jira-common-performance';
import Placeholder from '@atlassian/jira-placeholder';
import { createAri } from '@atlassian/jira-platform-ari';
import { SetCustomMark } from '@atlassian/jira-providers-spa-apdex-analytics/src';
import RedirectIfProjectArchived from '@atlassian/jira-redirect-archived-project';
import { useRelayResource } from '@atlassian/jira-relay-utils';
import type { aggBacklogDataQuery } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import VERSION_FILTER_RELAY_QUERY, {
	type viewBacklogVersionFilterRelayQuery,
} from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { Redirect, usePathParam } from '@atlassian/jira-router';
import {
	customFiltersResource,
	backlogRelayResource,
} from '@atlassian/jira-router-resources-next-gen-backlog';
import { NextGenBacklogSkeleton as BacklogSkeleton } from '@atlassian/jira-skeletons';
import BacklogPage from '@atlassian/jira-software-backlog';
import {
	useGetHasSuspended,
	SuspenseTrackerProvider,
	SuspenseTrackerFallback,
} from '@atlassian/jira-software-suspense-tracker';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product';
import { SpaStatePageReady, useSpaStateTransition } from '@atlassian/jira-spa-state-controller';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	BACKLOG_PREFETCHED_DATA_READY_MARK,
	USE_RESOURCE_READY_MARK,
	USE_RESOURCE_START_MARK,
} from '../../../../common/constants';
import { getBoardUrl, useOnce, getIdFromAri } from '../../../../common/utils';
import { BacklogCriticalDataBoundary } from './error-boundary';
import { useGetDataFromAGG } from './use-get-data-from-agg';
import { isBacklogDisabledFromAggData } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bindToOnJiraGlobalIssueCreateClose = (callback: any) => {
	// window.require is not available on initial script load.
	// eslint-disable-next-line jira/wrm/no-load-bridge
	loadBridge({
		name: 'jira/util/events',
		wrmKeys: 'wrc!jira.webresources:jira-events',
	}).then((Events) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		Events?.bind('QuickCreateIssue.sessionComplete', callback);
	});
};

export const BacklogAPIConsumerWithRelay = ({
	backlogQueryReference,
}: {
	backlogQueryReference: PreloadedQuery<aggBacklogDataQuery>;
}) => {
	const [projectKey] = usePathParam('projectKey');
	const [boardIdStr] = usePathParam('boardId');
	const boardId = Number(boardIdStr);

	const tenantContext = useTenantContext();

	/* eslint-disable @atlassian/relay/unused-fields */
	const aggData = usePreloadedQuery(
		graphql`
			query aggBacklogDataQuery($boardId: ID!, $cloudId: ID!) {
				boardScope(boardId: $boardId) @required(action: THROW) {
					sprints(state: [ACTIVE, FUTURE]) @required(action: THROW) {
						id @required(action: THROW)
						name @required(action: THROW)
						goal
						startDate
						endDate
						daysRemaining
						cards {
							id @required(action: THROW)
							key @required(action: THROW)
							summary @required(action: THROW)
							labels @required(action: THROW)
							type @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							status @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								category @required(action: THROW)
							}
							assignee {
								accountId @required(action: THROW)
								name @required(action: THROW)
								picture @required(action: THROW)
							}
							flagged @required(action: THROW)
							done @required(action: THROW)
							parentId
							estimate {
								storyPoints
								originalEstimate {
									value @required(action: THROW)
									valueAsText @required(action: THROW)
								}
							}
							priority {
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							dueDate
							childCardsMetadata {
								complete @required(action: THROW)
								total @required(action: THROW)
							}
							fixVersionsIds @required(action: THROW)
						}
						sprintState @required(action: THROW)
					}
					backlog {
						boardIssueListKey @required(action: THROW)
						requestColumnMigration @required(action: THROW)
						cards @required(action: THROW) {
							id @required(action: THROW)
							key @required(action: THROW)
							summary @required(action: THROW)
							labels @required(action: THROW)
							type @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							status @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								category @required(action: THROW)
							}
							assignee {
								accountId @required(action: THROW)
								name @required(action: THROW)
								picture @required(action: THROW)
							}
							flagged @required(action: THROW)
							done @required(action: THROW)
							parentId
							estimate {
								storyPoints
								originalEstimate {
									value @required(action: THROW)
									valueAsText @required(action: THROW)
								}
							}
							priority {
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							dueDate
							childCardsMetadata {
								complete @required(action: THROW)
								total @required(action: THROW)
							}
							fixVersionsIds
						}
					}
					board @required(action: THROW) {
						cards @required(action: THROW) {
							id @required(action: THROW)
							key @required(action: THROW)
							summary @required(action: THROW)
							labels @required(action: THROW)
							type @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							status @required(action: THROW) {
								id @required(action: THROW)
								name @required(action: THROW)
								category @required(action: THROW)
							}
							assignee {
								accountId @required(action: THROW)
								name @required(action: THROW)
								picture @required(action: THROW)
							}
							flagged @required(action: THROW)
							done @required(action: THROW)
							parentId
							estimate {
								storyPoints
								originalEstimate {
									value @required(action: THROW)
									valueAsText @required(action: THROW)
								}
							}
							priority {
								name @required(action: THROW)
								iconUrl @required(action: THROW)
							}
							dueDate
							childCardsMetadata {
								complete @required(action: THROW)
								total @required(action: THROW)
							}
							fixVersionsIds
						}
						rankCustomFieldId @required(action: THROW)
					}
					cardParents @required(action: THROW) {
						id @required(action: THROW)
						key @required(action: THROW)
						summary @required(action: THROW)
						cardType @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							iconUrl @required(action: THROW)
						}
						color
					}
					currentUser @required(action: THROW) {
						permissions @required(action: THROW)
					}
					projectLocation @required(action: THROW) {
						id @required(action: THROW)
						key @required(action: THROW)
						name @required(action: THROW)
						cardTypes @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							iconUrl @required(action: THROW)
							hierarchyLevelType @required(action: THROW)
							hasRequiredFields @required(action: THROW)
						}
					}
					features @required(action: THROW) {
						key @required(action: THROW)
						status @required(action: THROW)
					}
					estimation @required(action: THROW) {
						current @required(action: THROW) {
							customFieldId
						}
						available @required(action: THROW) {
							statisticFieldId @required(action: THROW)
						}
					}
				}
				jira @required(action: THROW) {
					globalTimeTrackingSettings(cloudId: $cloudId) @required(action: THROW) {
						defaultUnit @required(action: THROW)
						workingDaysPerWeek @required(action: THROW)
						workingHoursPerDay @required(action: THROW)
						defaultFormat @required(action: THROW)
					}
				}
			}
		`,
		backlogQueryReference,
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	useOnce(() => {
		setMark(BACKLOG_PREFETCHED_DATA_READY_MARK);
		setMeasure(
			'use-resource-fetch-finish',
			USE_RESOURCE_START_MARK,
			BACKLOG_PREFETCHED_DATA_READY_MARK,
		);
	});

	const response = useRelayResource<viewBacklogCustomFiltersQuery>(customFiltersResource);
	const customFiltersQueryRef = response.queryReference;

	const [{ isInitialRender }] = useSpaStateTransition();
	const hasSuspended = useGetHasSuspended();
	const isCacheHit = !isInitialRender && !hasSuspended;

	const projectKeyFromContext = aggData?.boardScope.projectLocation.key || '';
	const projectNameFromContext = aggData?.boardScope.projectLocation.name || '';
	const projectIdFromContext = getIdFromAri(aggData?.boardScope.projectLocation.id || '');

	const [versionFilterRelayQueryRef, loadVersionFilterRelayQuery] =
		useQueryLoader<viewBacklogVersionFilterRelayQuery>(VERSION_FILTER_RELAY_QUERY);

	const getDataFromAGG = useGetDataFromAGG({ cloudId: tenantContext.cloudId, boardId });

	useEffect(() => {
		// only run the query if projectId exists in context
		// we load the query client-side because:
		//  1. The field relies on projectId which isn't available from the URL path
		//  2. We don't want version data to block the critical page load
		if (loadVersionFilterRelayQuery != null && projectIdFromContext.length > 0) {
			const jiraProjectId = createAri({
				resourceOwner: 'jira',
				cloudId: tenantContext.cloudId,
				resourceType: 'project',
				resourceId: `${projectIdFromContext}`,
			});

			loadVersionFilterRelayQuery({
				jiraProjectIds: [jiraProjectId],
				first: 500,
				after: null,
			});
		}
	}, [loadVersionFilterRelayQuery, projectIdFromContext, tenantContext.cloudId]);

	// redirect to the board only if the backlog is disabled, no redirect if timeout
	if (isBacklogDisabledFromAggData(aggData)) {
		const urlProjectKey = projectKey ?? projectKeyFromContext;
		const boardUrl = getBoardUrl(urlProjectKey, String(boardId));

		return <Redirect to={boardUrl} />;
	}

	const backlogEl = (
		<>
			<SubProductUpdater subProduct="software" />
			<SetCustomMark name="configApiReady" />
			{/* @ts-expect-error - TS2739 - Type '{ isCacheHit: boolean; bindToOnJiraGlobalIssueCreateClose: (callback: any) => void; projectKey: string; projectId: number; rapidViewId: string; locale: Locale; baseUrl: string; xsrfToken: string; ... 5 more ...; customFiltersQueryRef: PreloadedQuery<...> | ... 1 more ... | undefined; }' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
			<BacklogPage
				isCacheHit={isCacheHit}
				bindToOnJiraGlobalIssueCreateClose={bindToOnJiraGlobalIssueCreateClose}
				// Route match data that could be read inside of the app
				projectKey={projectKey ?? projectKeyFromContext}
				projectId={Number(projectIdFromContext)}
				rapidViewId={String(boardId)}
				// Tenant context data that could be read inside of the app
				locale={tenantContext.locale}
				baseUrl={tenantContext.baseUrl}
				xsrfToken={tenantContext.xsrfToken ?? ''}
				cloudId={tenantContext.cloudId ?? ''}
				atlassianAccountId={tenantContext.atlassianAccountId ?? null}
				prefetchedData={null}
				prefetchedAggData={aggData}
				isServer={__SERVER__}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
				getDataFromAGG={getDataFromAGG}
			/>
			<SpaStatePageReady />
		</>
	);

	return (
		<ProjectBreadcrumb
			projectKey={projectKeyFromContext}
			projectName={projectNameFromContext}
			projectId={projectIdFromContext}
			isSimplifiedProject
		>
			{backlogEl}
			<RedirectIfProjectArchived projectKey={projectKey ?? projectKeyFromContext ?? null} />
		</ProjectBreadcrumb>
	);
};

export const BacklogMetaAPIConsumerWithRelay = () => {
	useOnce(() => {
		setMark(USE_RESOURCE_START_MARK);
	});

	const response = useRelayResource<aggBacklogDataQuery>(backlogRelayResource);

	const backlogQueryReference = response.queryReference;

	useOnce(
		() => {
			setMark(USE_RESOURCE_READY_MARK);
			setMeasure('use-resource', USE_RESOURCE_START_MARK, USE_RESOURCE_READY_MARK);
		},
		() => !!backlogQueryReference,
	);

	if (backlogQueryReference != null) {
		return (
			<SuspenseTrackerProvider>
				<Placeholder
					name="backlog-api-consumer-with-relay"
					fallback={
						<>
							<BacklogSkeleton />
							<SuspenseTrackerFallback />
						</>
					}
				>
					<BacklogCriticalDataBoundary
						errorCaptureKey={backlogQueryReference.fetchKey.toString()}
						backlogRootQueryName={backlogQueryReference.name}
					>
						<BacklogAPIConsumerWithRelay backlogQueryReference={backlogQueryReference} />
					</BacklogCriticalDataBoundary>
				</Placeholder>
			</SuspenseTrackerProvider>
		);
	}
	return null;
};

export default BacklogMetaAPIConsumerWithRelay;
